// @flow
import React, { PureComponent } from 'react';
import { StyleSheet, TouchableOpacity, View, Dimensions } from 'react-native';
import { Link, Separator, Text } from 'trpc-react-native-ui';
import { differenceInCalendarDays } from 'date-fns';
import type { Usage } from 'digicel-kaizen-library';
import I18n from '../../../../lib/i18n';
import theme from '../../../../theme-config';
import UsageDoughnut from '../../../../components/usage/UsageDoughnut';
import TopUpDoughnut from '../../../../components/usage/TopUpDoughnut';
import type { PackageUsages } from '../../../../modules/Usage/selectors';
import { formatDaysLeft } from '../../../../lib/date-utils';
import formatDurationValue from '../../../../lib/usage-formatters/roaming-duration-usage-formatter';
import formatDataVoiceSMSLabel from '../../../../lib/DataVoiceSMSLabelFormatter';
import { usagesComparator } from '../../../../lib/sort-options';

const doughnutSize = Math.max(75, Dimensions.get('window').width / 5);

export default class RoamingFrame extends PureComponent<Props> {
  styles = getStyles();

  renderUsage = (usage: Usage) => (
    <UsageDoughnut
      key={usage.label}
      label={formatDataVoiceSMSLabel(usage.label, '_CREDIT')}
      current={usage.currentValue}
      max={usage.currentValue}
      unitType={usage.unitType}
      size={doughnutSize}
      hideMaxValue
    />
  );

  renderTopUp = (usage: Usage) => (
    <TopUpDoughnut
      key={usage.label}
      label={usage.label}
      current={usage.currentValue}
      unitType={usage.unitType}
      size={doughnutSize}
      endDateTime={usage.endDateTime}
      style={this.styles.travelUsage}
    />
  );

  filterRawUsages = (usage: Usage) => !usage.label.includes('RAW');

  renderDoughnuts = (type: BoosterType) => {
    const doughnutsToRender = {
      TRAVEL: usage => this.renderTopUp(usage),
      ROAM_LIKE_HOME: usage => this.renderUsage(usage),
    };

    return (
      <View style={this.styles.doughnuts}>
        {this.props.usages
          .filter(this.filterRawUsages)
          .sort(usagesComparator)
          .map(usage => doughnutsToRender[type](usage))}
      </View>
    );
  };

  render() {
    const styles = this.styles;
    const { onPress, totalRoamingPacks, title, endDateTime, type, status, displayDonut } = this.props;

    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.titleContainer} onPress={onPress}>
          <Text style={styles.text}>
            <Text style={styles.title}>
              {totalRoamingPacks}
              {title}
            </Text>
            {endDateTime && type === 'ROAM_LIKE_HOME' && (
              <Text>
                <Text> - </Text>
                {status === 'PENDING_ROAMING' ? (
                  <Text style={styles.pendingExpiration}>
                    {I18n.t('Dashboard.RoamingFrame.expires', {
                      days: formatDaysLeft(differenceInCalendarDays(endDateTime, Date.now())),
                    })}
                  </Text>
                ) : status === 'ROAMING' ? (
                  <Text style={styles.usageExpiration}>
                    {I18n.t('Dashboard.RoamingFrame.beforeExpiry', {
                      duration: formatDurationValue((endDateTime - Date.now()) / 1000),
                    })}
                  </Text>
                ) : null}
              </Text>
            )}
          </Text>
          <Link text={this.props.textLink} style={styles.text} />
        </TouchableOpacity>
        {displayDonut && (
          <View>
            <Separator />
            {this.renderDoughnuts(type)}
          </View>
        )}
      </View>
    );
  }
}

export type DispatchProps = { onPress: Function };

type BoosterType = 'TRAVEL' | 'ROAM_LIKE_HOME';

type Props = {
  packages: PackageUsages,
  textLink: ?string,
  displayDonut: boolean,
  totalRoamingPacks?: String,
  title: string,
  endDateTime: number,
  status: string,
  usages: Array<Usage>,
  type: BoosterType,
} & DispatchProps;

const getStyles = () =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.frame.backgroundColor,
      borderRadius: 3,
      paddingHorizontal: 10,
      marginBottom: 10,
    },
    titleContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 8,
      paddingRight: 2,
    },
    text: {
      fontSize: 14,
    },
    title: {
      fontSize: 14,
    },
    pendingExpiration: {
      color: theme.gray.normalLight,
    },
    usageExpiration: {
      color: theme.primaryColor,
    },
    doughnuts: {
      marginHorizontal: 15,
      marginVertical: 10,
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',
    },
    travelUsage: {
      width: doughnutSize + 20, // enough to show "out of 100 sms" on one line
      marginRight: 5,
      alignItems: 'center',
    },
  });
